(() => {
  const WRAP_VARIANT_ID = 43625289777358;
  const ROOT = window.Shopify?.routes?.root || '/';
  const CART_SCOPE_SELECTOR = '.cart-totals';
  const CART_LINES_UPDATE_EVENT = 'shopify:cart:lines-update';
  let scheduled = false;
  let giftOptionsOpen = false;

  const cartRequest = async (path, body) => {
    const response = await fetch(`${ROOT}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'same-origin'
    });

    if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
    return response.json();
  };

  const getCart = () => cartRequest('cart.js');
  const updateAttributes = (attributes) => cartRequest('cart/update.js', { attributes });
  const findWrapItem = (cart) => cart.items.find((item) => Number(item.variant_id) === WRAP_VARIANT_ID);

  const getCartSectionPayload = () => {
    const sectionIds = [...new Set(
      [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((component) => component.dataset.sectionId)
        .filter(Boolean)
    )];

    if (!sectionIds.length) return {};

    return {
      sections: sectionIds.join(','),
      sections_url: window.location.pathname
    };
  };

  const dispatchCartUpdate = (source, action, response, cart) => {
    const promise = Promise.resolve({
      cart,
      detail: {
        sections: response.sections || {},
        items: cart.items,
        itemCount: cart.item_count,
        source: 'petal-cart-gifting',
        didError: false
      }
    });

    const event = new Event(CART_LINES_UPDATE_EVENT, { bubbles: true });
    event.action = action;
    event.context = 'cart';
    event.lines = [];
    event.promise = promise;
    source.dispatchEvent(event);

    return promise;
  };

  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  const createMarkup = (cart) => {
    const attributes = cart.attributes || {};
    const isGift = attributes['Is this a gift?'] === 'Yes';
    const message = attributes['Gift message'] || '';
    const wrapItem = findWrapItem(cart);
    const shouldOpen = giftOptionsOpen || isGift || Boolean(message) || Boolean(wrapItem);
    const id = `petal-gift-message-${Math.random().toString(36).slice(2)}`;

    const wrapper = document.createElement('details');
    wrapper.className = 'petal-cart-gifting';
    wrapper.setAttribute('aria-label', 'Gift options');
    if (shouldOpen) wrapper.open = true;

    wrapper.innerHTML = `
      <summary class="petal-cart-gifting__summary">
        <span class="petal-cart-gifting__label cart-primary-typography">Gift options</span>
        <span class="petal-cart-gifting__icon cart-totals__icon icon-plus" aria-hidden="true">
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path class="vertical" d="M2.75 7H11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"></path>
            <path class="horizontal" d="M7 2.75L7 11.25" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round"></path>
          </svg>
        </span>
      </summary>
      <div class="petal-cart-gifting__body">
        <p class="petal-cart-gifting__intro">Add a complimentary message or optional gift wrapping to this order.</p>

        <label class="petal-cart-gifting__choice">
          <input type="checkbox" data-petal-gift-toggle ${isGift ? 'checked' : ''}>
          <span>
            <strong>This order is a gift</strong>
            <small>A complimentary message card will be included.</small>
          </span>
        </label>

        <div class="petal-cart-gifting__message" data-petal-message-wrap ${isGift ? '' : 'hidden'}>
          <label for="${id}">Gift message</label>
          <textarea id="${id}" maxlength="200" rows="4" data-petal-gift-message placeholder="Write a short message for the recipient">${escapeHtml(message)}</textarea>
          <div class="petal-cart-gifting__counter"><span data-petal-message-count>${message.length}</span>/200</div>
        </div>

        <label class="petal-cart-gifting__choice">
          <input type="checkbox" data-petal-wrap-toggle ${wrapItem ? 'checked' : ''}>
          <span>
            <strong>Add gift wrapping — R50 per order</strong>
            <small>One gift-wrapping service will be added to this order.</small>
          </span>
        </label>

        <p class="petal-cart-gifting__status" data-petal-gift-status aria-live="polite"></p>
      </div>
    `;

    return wrapper;
  };

  const setBusy = (wrapper, busy, text = '') => {
    wrapper.setAttribute('aria-busy', busy ? 'true' : 'false');
    wrapper.querySelectorAll('input, textarea').forEach((element) => {
      element.disabled = busy;
    });
    const status = wrapper.querySelector('[data-petal-gift-status]');
    if (status) status.textContent = text;
  };

  const showError = (wrapper) => {
    setBusy(wrapper, false, 'We could not update the gift options. Please try again.');
  };

  const bindControls = (wrapper) => {
    const giftToggle = wrapper.querySelector('[data-petal-gift-toggle]');
    const wrapToggle = wrapper.querySelector('[data-petal-wrap-toggle]');
    const messageWrap = wrapper.querySelector('[data-petal-message-wrap]');
    const textarea = wrapper.querySelector('[data-petal-gift-message]');
    const counter = wrapper.querySelector('[data-petal-message-count]');
    let messageTimer;

    wrapper.addEventListener('toggle', () => {
      giftOptionsOpen = wrapper.open;
    });

    giftToggle?.addEventListener('change', async () => {
      const checked = giftToggle.checked;
      messageWrap.hidden = !checked;
      setBusy(wrapper, true, 'Saving…');

      try {
        await updateAttributes({
          'Is this a gift?': checked ? 'Yes' : '',
          'Gift message': checked ? textarea.value.trim() : ''
        });
        setBusy(wrapper, false, 'Gift details saved.');
        if (checked) textarea.focus();
      } catch (_) {
        giftToggle.checked = !checked;
        messageWrap.hidden = checked;
        showError(wrapper);
      }
    });

    textarea?.addEventListener('input', () => {
      if (counter) counter.textContent = String(textarea.value.length);
      window.clearTimeout(messageTimer);
      messageTimer = window.setTimeout(async () => {
        if (!giftToggle.checked) return;
        setBusy(wrapper, true, 'Saving…');
        try {
          await updateAttributes({
            'Is this a gift?': 'Yes',
            'Gift message': textarea.value.trim()
          });
          setBusy(wrapper, false, 'Message saved.');
        } catch (_) {
          showError(wrapper);
        }
      }, 550);
    });

    wrapToggle?.addEventListener('change', async () => {
      const checked = wrapToggle.checked;
      giftOptionsOpen = true;
      setBusy(wrapper, true, checked ? 'Adding gift wrapping…' : 'Removing gift wrapping…');

      try {
        const currentCart = await getCart();
        const wrapItem = findWrapItem(currentCart);
        let response;
        let action;

        if (checked && !wrapItem) {
          response = await cartRequest('cart/add.js', {
            items: [{
              id: WRAP_VARIANT_ID,
              quantity: 1,
              properties: { _petal_gift_wrap: 'true' }
            }],
            ...getCartSectionPayload()
          });
          action = 'add';
        } else if (checked && wrapItem && wrapItem.quantity !== 1) {
          response = await cartRequest('cart/change.js', {
            id: wrapItem.key,
            quantity: 1,
            ...getCartSectionPayload()
          });
          action = 'update';
        } else if (!checked && wrapItem) {
          response = await cartRequest('cart/change.js', {
            id: wrapItem.key,
            quantity: 0,
            ...getCartSectionPayload()
          });
          action = 'remove';
        } else {
          setBusy(wrapper, false, checked ? 'Gift wrapping is already added.' : 'Gift wrapping is not in the cart.');
          return;
        }

        const updatedCart = await getCart();
        await dispatchCartUpdate(wrapper, action, response, updatedCart);
        setBusy(wrapper, false, checked ? 'Gift wrapping added.' : 'Gift wrapping removed.');
      } catch (_) {
        wrapToggle.checked = !checked;
        showError(wrapper);
      }
    });
  };

  const insertGiftOptions = (target, wrapper) => {
    const actions = target.querySelector('.cart-actions');

    if (actions) {
      const divider = document.createElement('div');
      divider.className = 'cart-actions__divider petal-cart-gifting__divider';
      actions.append(divider, wrapper);
      return;
    }

    wrapper.classList.add('petal-cart-gifting--standalone');
    const insertionPoint = target.querySelector('.cart-totals__container');
    target.insertBefore(wrapper, insertionPoint || target.firstChild);
  };

  const enhanceCart = async () => {
    const targets = [...document.querySelectorAll(CART_SCOPE_SELECTOR)].filter(
      (target) => !target.querySelector('.petal-cart-gifting')
    );
    if (!targets.length) return;

    try {
      const cart = await getCart();
      targets.forEach((target) => {
        if (target.querySelector('.petal-cart-gifting')) return;
        const wrapper = createMarkup(cart);
        insertGiftOptions(target, wrapper);
        bindControls(wrapper);
      });
    } catch (_) {
      // Leave the native cart untouched if Shopify's cart endpoint is unavailable.
    }
  };

  const addProductGiftNotice = () => {
    if (!window.location.pathname.startsWith('/products/')) return;
    if (document.querySelector('.petal-product-gift-note')) return;

    const addButton = document.querySelector('.add-to-cart-button, [name="add"], product-form button[type="submit"]');
    if (!addButton) return;

    const paymentButton = document.querySelector('.shopify-payment-button');
    const purchaseArea = paymentButton || addButton.closest('.product-form-buttons, .buy-buttons-block, product-form') || addButton;
    const note = document.createElement('p');
    note.className = 'petal-product-gift-note';
    note.innerHTML = '<strong>Sending this as a gift?</strong> Add a complimentary message in your cart. Optional gift wrapping is available for R50 per order.';
    //purchaseArea.insertAdjacentElement('afterend', note);
  };

  const enhance = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(() => {
      scheduled = false;
      addProductGiftNotice();
      enhanceCart();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhance, { once: true });
  } else {
    enhance();
  }

  document.addEventListener('shopify:section:load', enhance);
  document.addEventListener('cart:update', enhance);
  document.addEventListener(CART_LINES_UPDATE_EVENT, enhance);

  new MutationObserver(enhance).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
